import React, { useState, useEffect, useMemo } from 'react';

const MyCalendar = ({ start, setStart, end, setEnd, selectFacility, setSelectEnd, reservedList = [] }) => {
  const [date, setDate] = useState(new Date());
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [startOfMonth, setStartOfMonth] = useState(null);
  const [endOfMonth, setEndOfMonth] = useState(null);
  const [startDay, setStartDay] = useState(null);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);

  useEffect(() => {
    formatDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const pad2 = (n) => (String(n).length === 1 ? '0' + n : String(n));

  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    return `${y}-${m}-${dd}`;
  };

  // ✅ 예약 리스트를 날짜별 Map으로 만들어서 빠르게 참조
  const reservedMap = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(reservedList)) return map;

    reservedList.forEach((r) => {
      if (!r?.start_date || !r?.end_date) return;

      const s = new Date(r.start_date);
      const e = new Date(r.end_date);

      const cur = new Date(s);
      cur.setHours(0, 0, 0, 0);

      const endD = new Date(e);
      endD.setHours(0, 0, 0, 0);

      while (cur <= endD) {
        const key = toYMD(cur);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(r);
        cur.setDate(cur.getDate() + 1);
      }
    });

    return map;
  }, [reservedList]);

  const formatDate = () => {
    const currentYear = date.getFullYear().toString();
    let currentMonth = (date.getMonth() + 1).toString();
    currentMonth = currentMonth.length === 1 ? '0' + currentMonth : currentMonth;

    const som = new Date(date.getFullYear(), date.getMonth(), 1);
    const eom = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const firstDayOfWeek = som.getDay();

    setYear(currentYear);
    setMonth(currentMonth);
    setStartOfMonth(som);
    setEndOfMonth(eom);
    setStartDay(firstDayOfWeek === 0 ? 1 - 7 : 1 - firstDayOfWeek);
  };

  const handleDayClick = (calendarDate) => {
    const ymd = toYMD(calendarDate);
    if (reservedMap.has(ymd)) return; // ✅ 예약일 클릭 차단

    if (!isSelectingEnd) {
      setStart(calendarDate);
      setEnd(null);
      setSelectEnd('');
      setIsSelectingEnd(true);
    } else {
      if (calendarDate < start) {
        setStart(calendarDate);
        setEnd(null);
      } else {
        setEnd(calendarDate);
        setIsSelectingEnd(false);
      }
    }
  };

  // ✅ “어디서 예약했는지” 필드가 프로젝트마다 다를 수 있어서 안전하게 fallback 처리
  const getReserveSource = (r) => {
    return (
      r?.reserve_source ||
      r?.reservation_source ||
      r?.reserve_from ||
      r?.channel ||
      r?.source ||
      r?.location ||
      r?.reserve_place ||
      ''
    );
  };

  const renderCalendar = () => {
    const weeks = [];
    let currentDay = startDay;

    for (let i = 0; i < 6; i++) {
      const days = [];
      let isWeekValid = false;

      for (let j = 0; j < 7; j++) {
        const calendarDate = new Date(date.getFullYear(), date.getMonth(), currentDay);
        const dayOfWeek = calendarDate.getDay();
        let classNames = 'day';

        if (calendarDate >= startOfMonth && calendarDate <= endOfMonth) {
          isWeekValid = true;

          const ymd = toYMD(calendarDate);
          const isReserved = reservedMap.has(ymd);
          const reserveItems = isReserved ? reservedMap.get(ymd) : [];

          // 공통: 오늘/주말/과거
          if (calendarDate.toDateString() === new Date().toDateString()) classNames += ' today';

          const isClickableBase = calendarDate.setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);
          let isClickable = isClickableBase;

          if (dayOfWeek === 0) classNames += ' sun';
          else if (dayOfWeek === 6) classNames += ' sat';

          if (selectFacility === null) {
            classNames += ' none';
            if (!isClickableBase) classNames += ' disabled';

            days.push(
              <td
                key={ymd}
                onClick={() => {
                  alert('시설을 선택해주세요.');
                }}
              >
                <span className={classNames}>
                  <span className="num">{calendarDate.getDate()}</span>
                </span>
              </td>
            );
          } else {
            // 선택 범위 표시
            if (start && calendarDate.toDateString() === start.toDateString()) classNames += ' start-date';
            if (end && calendarDate.toDateString() === end.toDateString()) classNames += ' end-date';
            if (start && end && calendarDate >= start && calendarDate <= end) classNames += ' in-range';

            // 예약이면 클릭 불가
            if (isReserved) {
              isClickable = false;
              classNames += ' reserved';
            } else {
              if (isClickableBase) classNames += ' possible';
              else classNames += ' disabled';
            }

            const first = reserveItems?.[0] || null;
            const nameText = first?.reservation_name
              ? `${first.reservation_name}${reserveItems.length > 1 ? ` 외 ${reserveItems.length - 1}` : ''}`
              : reserveItems.length > 0
              ? `예약 ${reserveItems.length}건`
              : '';

            const sourceText = first ? getReserveSource(first) : '';

            days.push(
              <td key={ymd} onClick={isClickable ? () => handleDayClick(calendarDate) : undefined}>
                {/* ✅ 날짜(원/선택영역)는 span.day가 전담 — 예약 UI는 td 하단 오버레이로 분리 */}
                <span className={classNames}>
                  <span className="num">{calendarDate.getDate()}</span>
                </span>

                {isReserved && (
                  <div className="reserve_below">
                    <div className="reserve_badge_red">이미 예약됨</div>
                    {nameText && <div className="reserve_name">{nameText}</div>}
                    {sourceText && <div className="reserve_source">{sourceText}</div>}
                  </div>
                )}
              </td>
            );
          }
        } else {
          days.push(
            <td key={`${i}-${j}`}>
              <span className="day"></span>
            </td>
          );
        }

        currentDay++;
      }

      if (days.some((day) => day.props.children !== undefined) && isWeekValid) {
        weeks.push(<tr key={i}>{days}</tr>);
      }
    }

    return weeks;
  };

  const handleMonthChange = (type) => {
    if (type === 'prev') setDate(new Date(date.getFullYear(), date.getMonth() - 1, 1));
    else setDate(new Date(date.getFullYear(), date.getMonth() + 1, 1));
  };

  return (
    <div className="myCalendar">
      <div className="calendar_date">
        <div className="calendar_date_yymm">
          {year}.{month}
        </div>
        <div className="calendar_change">
          <div className="calendar_prev" onClick={() => handleMonthChange('prev')}></div>
          <div className="calendar_next" onClick={() => handleMonthChange('next')}></div>
        </div>
      </div>

      <table className="calendar_box">
        <thead>
          <tr>
            <th className="sun">일</th>
            <th>월</th>
            <th>화</th>
            <th>수</th>
            <th>목</th>
            <th>금</th>
            <th className="sat">토</th>
          </tr>
        </thead>
        <tbody className="calendar_List">{renderCalendar()}</tbody>
      </table>
    </div>
  );
};

export default MyCalendar;
